import { computed, inject, ref, type ComputedRef, type Ref } from 'vue';
import { useRouter } from 'vue-router';
import type { AjaxClient } from 'types/injects/ajaxClient.type';
import type { Translator } from 'types/injects/translator.type';
import type { PresetSupplier, ThirdAutocompleteResult } from 'types/entities/company/third.type';
import type { AjaxAutocompleteParams, SupplierAction, SupplierOption } from 'composables/ocr/types';

interface ThirdInputValues {
    corp_name?: string;
}

interface ThirdGlobal {
    create: (
        type: 'client' | 'prospect' | 'supplier',
        docallback: string,
        event: Event | null,
        prefill: object | null,
        hasProspectionPlugin: 'Y' | 'N' | null,
        inputsValues: ThirdInputValues,
    ) => void;
}

interface UseOcrSupplierSearchOptions {
    presetSupplier: Ref<PresetSupplier[]>;
    currentSupplierId: Ref<number | null>;
    canCreateSupplier: Ref<boolean>;
}

interface UseOcrSupplierSearchReturn {
    supplierResults: Ref<SupplierOption[]>;
    supplierLoading: Ref<boolean>;
    supplierActions: Ref<SupplierAction[]>;
    supplierSelectKey: Ref<number>;
    remoteSupplier: Ref<string>;
    presetSupplierOptions: ComputedRef<SupplierOption[]>;
    supplierDefaultValues: ComputedRef<SupplierOption[]>;
    onSupplierSearch: (query: string) => void;
    onSupplierFocus: (query?: string) => void;
    bumpSupplierSelectKey: () => void;
    addSupplier: () => void;
}

export const useOcrSupplierSearch = (
    options: UseOcrSupplierSearchOptions,
): UseOcrSupplierSearchReturn => {
    const $t = inject('$t') as Translator;
    const $ajax = inject('$ajax') as AjaxClient;
    const router = useRouter();

    const supplierResults = ref<SupplierOption[]>([]);
    const supplierLoading = ref(false);
    const supplierActions = ref<SupplierAction[]>([]);
    const supplierSelectKey = ref(0);
    const remoteSupplier = ref('');

    const autocompleteRoute = computed<string>(
        () => router.resolve({ name: 'legacy.third.autocomplete' }).href,
    );

    const presetSupplierOptions = computed<SupplierOption[]>(() =>
        options.presetSupplier.value.map((supplier) => ({
            value: supplier.id,
            label: supplier.name,
        })),
    );

    const supplierDefaultValues = computed<SupplierOption[]>(() => {
        const preset = options.presetSupplier.value.find(
            (supplier) => supplier.id === options.currentSupplierId.value,
        );
        return preset ? [{ value: preset.id, label: preset.name }] : [];
    });

    const addSupplier = (): void => {
        (Third as ThirdGlobal).create('supplier', 'noRedirect', null, null, null, {
            corp_name: remoteSupplier.value,
        });
    };

    const updateSupplierActions = (query: string): void => {
        supplierActions.value = options.canCreateSupplier.value
            ? [
                  {
                      label: $t
                          .__('ocr.OcrVerificationForm.create_supplier', { supplier: query || '' })
                          .trim(),
                      callback: addSupplier,
                  },
              ]
            : [];
    };

    const onSupplierSearch = (query: string): void => {
        remoteSupplier.value = query;

        if (!query) {
            supplierResults.value = [...presetSupplierOptions.value];
            updateSupplierActions('');
            supplierLoading.value = false;
            return;
        }

        supplierLoading.value = true;

        $ajax.get<ThirdAutocompleteResult[], AjaxAutocompleteParams>(
            autocompleteRoute.value,
            { q: query, types: ['supplier'] },
            (response) => {
                supplierResults.value = response.map((supplier) => ({
                    value: supplier.id,
                    label: supplier.name,
                }));
                updateSupplierActions(query);
                supplierLoading.value = false;
            },
            () => {
                supplierLoading.value = false;
            },
        );
    };

    const onSupplierFocus = (query?: string): void => {
        onSupplierSearch(query ?? '');
    };

    const bumpSupplierSelectKey = (): void => {
        supplierSelectKey.value++;
    };

    return {
        supplierResults,
        supplierLoading,
        supplierActions,
        supplierSelectKey,
        remoteSupplier,
        presetSupplierOptions,
        supplierDefaultValues,
        onSupplierSearch,
        onSupplierFocus,
        bumpSupplierSelectKey,
        addSupplier,
    };
};
